<template>
    <Head title="Work Orders" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center gap-3">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white dark:text-white">Work Orders</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Track and manage maintenance work orders</p>
                        </div>
                        <InfoButton title="Work Orders Overview" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Work Orders page is your central hub for managing all maintenance activities.
                                It provides a comprehensive view of corrective, preventive, and emergency maintenance
                                work orders across your organization. Use this page to track work progress, assign
                                technicians, monitor priorities, and ensure timely completion of maintenance tasks.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>View Tabs:</strong> Switch between All, My Work Orders, Team, Unassigned,
                                  or Scheduled views to focus on relevant work orders<br>
                                • <strong>Search:</strong> Quickly find work orders by number, title, description,
                                  or asset name/tag<br>
                                • <strong>Filter:</strong> Use dropdown filters to narrow results by Type (corrective,
                                  preventive, etc.), Status (open, in progress, completed), Priority, Asset, or
                                  Assigned Technician<br>
                                • <strong>Bulk Selection:</strong> Check multiple work orders to perform bulk actions<br>
                                • <strong>Calendar View:</strong> Click the Calendar button to see work orders in
                                  a visual timeline<br>
                                • <strong>Create Work Order:</strong> Click "New Work Order" to initiate a maintenance
                                  request
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • Use the <strong>My Work Orders</strong> tab to quickly see tasks assigned to you<br>
                                • The <strong>Unassigned</strong> tab helps supervisors identify work orders that
                                  need assignment<br>
                                • <strong>Scheduled</strong> tab shows all future-planned maintenance activities<br>
                                • Apply multiple filters simultaneously for precise results (e.g., "High priority +
                                  In progress + Specific asset")<br>
                                • Click "Clear Filters" to reset all filters and see the full list<br>
                                • Red due dates indicate overdue work orders requiring immediate attention<br>
                                • Each work order's color-coded badges (type, priority, status) provide quick
                                  visual identification
                            </p>
                        </InfoButton>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Link
                            :href="route('work-orders.calendar')"
                            class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Calendar
                        </Link>
                        <Link
                            v-if="$page.props.auth.permissions.includes('maintenance.work_orders.create')"
                            :href="route('work-orders.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 transition"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Work Order
                        </Link>
                    </div>
                </div>

                <!-- View Tabs -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 border border-gray-200 dark:border-gray-700">
                    <div class="border-b border-gray-200 dark:border-gray-700">
                        <nav class="-mb-px flex space-x-8 px-6">
                            <button
                                v-for="viewOption in viewOptions"
                                :key="viewOption.value"
                                @click="changeView(viewOption.value)"
                                :class="[
                                    'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                                    view === viewOption.value
                                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                ]"
                            >
                                {{ viewOption.label }}
                                <span
                                    v-if="viewOption.count !== undefined"
                                    :class="[
                                        'ml-2 py-0.5 px-2 rounded-full text-xs',
                                        view === viewOption.value
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    ]"
                                >
                                    {{ viewOption.count }}
                                </span>
                            </button>
                        </nav>
                    </div>
                </div>

                <!-- Filters and Search -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 border border-gray-200 dark:border-gray-700">
                    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <!-- Search -->
                            <div class="flex-1 max-w-lg">
                                <div class="relative">
                                    <input
                                        v-model="searchQuery"
                                        type="text"
                                        placeholder="Search work orders..."
                                        class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                                        @input="debounceSearch"
                                    />
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- Bulk Actions -->
                            <div v-if="selectedWorkOrders.length > 0" class="flex items-center space-x-2">
                                <span class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ selectedWorkOrders.length }} selected
                                </span>
                                <button
                                    @click="clearSelection"
                                    class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filter Pills -->
                    <div class="p-4 flex flex-wrap gap-2">
                        <select
                            v-model="selectedFilters.type"
                            class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Types</option>
                            <option value="corrective">Corrective</option>
                            <option value="preventive">Preventive</option>
                            <option value="predictive">Predictive</option>
                            <option value="inspection">Inspection</option>
                            <option value="calibration">Calibration</option>
                            <option value="emergency">Emergency</option>
                        </select>

                        <select
                            v-model="selectedFilters.status"
                            class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Status</option>
                            <option value="open">Open</option>
                            <option value="assigned">Assigned</option>
                            <option value="in_progress">In Progress</option>
                            <option value="on_hold">On Hold</option>
                            <option value="pending_review">Pending Review</option>
                            <option value="completed">Completed</option>
                            <option value="closed">Closed</option>
                        </select>

                        <select
                            v-model="selectedFilters.priority"
                            class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Priorities</option>
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                        </select>

                        <select
                            v-model="selectedFilters.sla_status"
                            class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All SLA Status</option>
                            <option value="on_track">On Track</option>
                            <option value="warning">Warning</option>
                            <option value="critical">Critical</option>
                            <option value="breached">Breached</option>
                            <option value="met">Met</option>
                            <option value="breached_met">Breached (Met)</option>
                        </select>

                        <select
                            v-model="selectedFilters.assigned_to"
                            class="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Technicians</option>
                            <option v-for="tech in technicians" :key="tech.id" :value="tech.id">
                                {{ tech.name }}
                            </option>
                        </select>

                        <button
                            v-if="hasActiveFilters"
                            @click="clearFilters"
                            class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                <!-- Work Orders Table -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden mb-6 border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" class="w-12 px-6 py-3">
                                    <input
                                        type="checkbox"
                                        :checked="allSelected"
                                        @change="toggleSelectAll"
                                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Work Order
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Service & Type
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Priority
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    <div class="flex items-center gap-2">
                                        SLA Status
                                        <InfoButton title="SLA Status Indicators" variant="compact">
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">SLA Status Indicators</strong><br>
                                                • <strong>On Track (Green):</strong> 50%+ time remaining<br>
                                                • <strong>Warning (Yellow):</strong> 20-49% time remaining<br>
                                                • <strong>Critical (Orange):</strong> Less than 20% time remaining<br>
                                                • <strong>Breached (Red):</strong> Deadline passed<br>
                                                • <strong>Met (Blue):</strong> Completed within SLA<br>
                                                • <strong>Breached (Met) (Purple):</strong> Completed but SLA was breached
                                            </p>
                                        </InfoButton>
                                    </div>
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Assigned To
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    <button
                                        @click="sortBy('created_at')"
                                        class="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300"
                                    >
                                        Request Date
                                        <svg
                                            v-if="sortField === 'created_at' && sortDirection === 'asc'"
                                            class="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
                                        </svg>
                                        <svg
                                            v-else-if="sortField === 'created_at' && sortDirection === 'desc'"
                                            class="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                        <svg
                                            v-else
                                            class="w-4 h-4 text-gray-400 dark:text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                        </svg>
                                    </button>
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr
                                v-for="workOrder in workOrders.data"
                                :key="workOrder.id"
                                :class="[
                                    'hover:bg-gray-50 dark:hover:bg-gray-700',
                                    selectedWorkOrders.includes(workOrder.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                ]"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        :checked="selectedWorkOrders.includes(workOrder.id)"
                                        @change="toggleSelect(workOrder.id)"
                                        class="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                </td>
                                <td class="px-6 py-4">
                                    <Link
                                        :href="route('work-orders.show', workOrder.id)"
                                        class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                    >
                                        {{ workOrder.work_order_number }}
                                    </Link>
                                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">{{ workOrder.title }}</p>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="flex flex-col gap-1">
                                        <span v-if="workOrder.service" class="text-sm text-gray-900 dark:text-white">
                                            {{ workOrder.service.name }}
                                        </span>
                                        <span v-else class="text-sm text-gray-400 dark:text-gray-500">No service</span>
                                        <span
                                            :class="[
                                                'px-2 py-1 text-xs font-medium rounded-full inline-block w-fit',
                                                getTypeClass(workOrder.type)
                                            ]"
                                        >
                                            {{ workOrder.type_label }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getPriorityClass(workOrder.priority)
                                        ]"
                                    >
                                        {{ workOrder.priority_label }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getStatusClass(workOrder.status)
                                        ]"
                                    >
                                        {{ workOrder.status_label }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="workOrder.sla_policy_id"
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getSlaStatusClass(workOrder.sla_status)
                                        ]"
                                        :title="workOrder.sla_time_remaining"
                                    >
                                        {{ getSlaStatusLabel(workOrder.sla_status) }}
                                    </span>
                                    <span v-else class="text-xs text-gray-400 dark:text-gray-500">No SLA</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span v-if="workOrder.assigned_to_name" class="text-gray-900 dark:text-white">
                                        {{ workOrder.assigned_to_name }}
                                    </span>
                                    <span v-else class="text-gray-400 dark:text-gray-500">Unassigned</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {{ formatDate(workOrder.created_at) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link
                                        :href="route('work-orders.show', workOrder.id)"
                                        class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                    >
                                        View
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="workOrders.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-lg shadow">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <Link
                            v-if="workOrders.prev_page_url"
                            :href="workOrders.prev_page_url"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Previous
                        </Link>
                        <Link
                            v-if="workOrders.next_page_url"
                            :href="workOrders.next_page_url"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Next
                        </Link>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-gray-300">
                                Showing
                                <span class="font-medium">{{ workOrders.from }}</span>
                                to
                                <span class="font-medium">{{ workOrders.to }}</span>
                                of
                                <span class="font-medium">{{ workOrders.total }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <template v-for="link in workOrders.links" :key="link.label">
                                    <Link
                                        v-if="link.url"
                                        :href="link.url"
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                            link.active
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                                        ]"
                                        v-html="link.label"
                                    />
                                    <span
                                        v-else
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                            'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                                        ]"
                                        v-html="link.label"
                                    />
                                </template>
                            </nav>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="workOrders.data.length === 0" class="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center border border-gray-200 dark:border-gray-700">
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No work orders found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new work order.</p>
                    <div class="mt-6">
                        <Link
                            v-if="$page.props.auth.permissions.includes('maintenance.work_orders.create')"
                            :href="route('work-orders.create')"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Work Order
                        </Link>
                    </div>
                </div>
            </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    workOrders: Object,
    assets: Array,
    locations: Array,
    technicians: Array,
    view: String,
    filters: Object,
});

// Use timezone composable for date formatting
const { formatDate: formatDateInTZ } = useTimezone();

const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    type: props.filters.type || '',
    status: props.filters.status || '',
    priority: props.filters.priority || '',
    sla_status: props.filters.sla_status || '',
    asset_id: props.filters.asset_id || '',
    assigned_to: props.filters.assigned_to || '',
});
const selectedWorkOrders = ref([]);
const sortField = ref(props.filters.sort_by || '');
const sortDirection = ref(props.filters.sort_order || 'desc');

const viewOptions = [
    { value: 'all', label: 'All' },
    { value: 'my', label: 'My Work Orders' },
    { value: 'team', label: 'Team' },
    { value: 'unassigned', label: 'Unassigned' },
    { value: 'scheduled', label: 'Scheduled' },
];

const allSelected = computed(() => {
    return props.workOrders.data.length > 0 &&
           selectedWorkOrders.value.length === props.workOrders.data.length;
});

const hasActiveFilters = computed(() => {
    return selectedFilters.value.type ||
           selectedFilters.value.status ||
           selectedFilters.value.priority ||
           selectedFilters.value.sla_status ||
           selectedFilters.value.asset_id ||
           selectedFilters.value.assigned_to ||
           searchQuery.value;
});

let searchTimeout = null;

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const changeView = (newView) => {
    router.get(route('work-orders.index'), {
        view: newView,
        search: searchQuery.value,
        ...selectedFilters.value,
        sort_by: sortField.value,
        sort_order: sortDirection.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const applyFilters = () => {
    router.get(route('work-orders.index'), {
        view: props.view,
        search: searchQuery.value,
        ...selectedFilters.value,
        sort_by: sortField.value,
        sort_order: sortDirection.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const sortBy = (field) => {
    if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortField.value = field;
        sortDirection.value = 'desc';
    }
    applyFilters();
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        type: '',
        status: '',
        priority: '',
        asset_id: '',
        assigned_to: '',
    };
    sortField.value = '';
    sortDirection.value = 'desc';
    applyFilters();
};

const toggleSelect = (id) => {
    const index = selectedWorkOrders.value.indexOf(id);
    if (index > -1) {
        selectedWorkOrders.value.splice(index, 1);
    } else {
        selectedWorkOrders.value.push(id);
    }
};

const toggleSelectAll = () => {
    if (allSelected.value) {
        selectedWorkOrders.value = [];
    } else {
        selectedWorkOrders.value = props.workOrders.data.map(wo => wo.id);
    }
};

const clearSelection = () => {
    selectedWorkOrders.value = [];
};

const getTypeClass = (type) => {
    const classes = {
        corrective: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        preventive: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        predictive: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        inspection: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        calibration: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        emergency: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getPriorityClass = (priority) => {
    const classes = {
        low: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        normal: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        urgent: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[priority] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getStatusClass = (status) => {
    const classes = {
        draft: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        open: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        assigned: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300',
        in_progress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        on_hold: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        pending_review: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
        completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        closed: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const isDueDateOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
};

// Format date in company timezone (timezone-aware using company settings)
const formatDate = (date) => {
    return formatDateInTZ(date); // Now uses company timezone!
};

const getSlaStatusClass = (status) => {
    const classes = {
        on_track: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        critical: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
        breached: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        met: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        breached_met: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

const getSlaStatusLabel = (status) => {
    const labels = {
        on_track: 'On Track',
        warning: 'Warning',
        critical: 'Critical',
        breached: 'Breached',
        met: 'Met',
        breached_met: 'Breached (Met)',
    };
    return labels[status] || status;
};
</script>
